import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { KeyRound } from "lucide-react";
import api from "../lib/api";
import { useLang } from "../context/LanguageContext";

export default function ResetPage() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const inputCls =
    "w-full rounded-md border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-800 placeholder-slate-400 outline-none transition-colors focus:border-[#2563EB] focus:bg-white";

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { setError(t.reset_mismatch); return; }
    setBusy(true); setError("");
    try {
      await api.post("/auth/reset", { token, new_password: password });
      setDone(true);
      toast.success(t.reset_done);
      setTimeout(() => navigate("/auth"), 2000);
    } catch (err) {
      setError(err.response?.data?.detail || t.reset_invalid);
    } finally { setBusy(false); }
  };

  return (
    <div className="flex min-h-[80vh] items-center justify-center px-5 pb-16 pt-28" data-testid="reset-page">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-md rounded-xl border border-slate-200/90 bg-white p-8 shadow-[0_12px_40px_-8px_rgba(15,23,42,0.1)] sm:p-9"
      >
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#2563EB]">{t.login_tag}</p>
        <h1 className="mt-2 flex items-center gap-2 font-display text-2xl font-bold text-slate-900">
          <KeyRound size={20} className="text-[#2563EB]" /> {t.reset_title}
        </h1>
        {done ? (
          <p className="mt-6 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700" data-testid="reset-success">
            {t.reset_done}
          </p>
        ) : (
          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="reset-form">
            <input type="password" required minLength={6} autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="reset-password-input" placeholder={t.reset_new} className={inputCls} />
            <input type="password" required minLength={6} autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="reset-confirm-input" placeholder={t.reset_confirm} className={inputCls} />
            {error && <p className="text-sm text-rose-500" data-testid="reset-error">{error}</p>}
            <button type="submit" disabled={busy || !token} data-testid="reset-submit-button" className="w-full rounded-md bg-[#1E3A8A] py-3.5 font-mono text-xs uppercase tracking-[0.18em] text-white transition-colors hover:bg-[#2563EB] disabled:opacity-50">
              {busy ? t.creds_saving : t.reset_btn}
            </button>
            {!token && <p className="text-sm text-rose-500">{t.reset_invalid}</p>}
          </form>
        )}
      </motion.div>
    </div>
  );
}
